import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Input, Select, SelectItem, Spinner } from '@heroui/react';
import { useCreateMailbox, useMailboxDomainSummary, useUpdateMailbox } from '@/features/mailboxes/hooks';
import { createMailboxSchema, editMailboxSchema, GB, type CreateMailboxValues, type EditMailboxValues } from '@/features/mailboxes/schema';
import { createMailboxError, type CreateMailboxProblem } from '@/features/mailboxes/createMailboxError';
import type { Mailbox } from '@/features/mailboxes/types';

export function MailboxFormModal({ isOpen, onClose, mailbox, defaultDomainId }: {
  isOpen: boolean; onClose: () => void; mailbox?: Mailbox | null;
  /** Preselect this domain (e.g. "Create mailbox" from a domain group). */
  defaultDomainId?: string;
}) {
  const isEdit = !!mailbox;
  const create = useCreateMailbox();
  const update = useUpdateMailbox(mailbox?.id ?? '');
  const summary = useMailboxDomainSummary();
  // Only this tenant's domains that can take mailboxes right now.
  const domains = useMemo(() => (summary.data ?? []).filter((d) => d.mailEnabled), [summary.data]);
  const [problem, setProblem] = useState<CreateMailboxProblem | null>(null);

  const createForm = useForm<CreateMailboxValues>({
    resolver: zodResolver(createMailboxSchema),
    defaultValues: { localPart: '', domainId: '', displayName: '', password: '', quotaGb: 5 },
  });
  const editForm = useForm<EditMailboxValues>({
    resolver: zodResolver(editMailboxSchema),
    defaultValues: { displayName: '', status: 'active', quotaGb: 5 },
  });

  useEffect(() => {
    setProblem(null);
    if (mailbox) editForm.reset({ displayName: mailbox.displayName, status: mailbox.status, quotaGb: Math.round(mailbox.quotaBytes / GB) });
    else createForm.reset({ localPart: '', domainId: '', displayName: '', password: '', quotaGb: 5 });
  }, [mailbox, isOpen]); // eslint-disable-line

  // Preselect the requested domain, or the only one available.
  const chosen = createForm.watch('domainId');
  useEffect(() => {
    if (isEdit || chosen || domains.length === 0) return;
    const pick = domains.find((d) => d.domainId === defaultDomainId) ?? (domains.length === 1 ? domains[0] : undefined);
    if (pick) createForm.setValue('domainId', pick.domainId, { shouldValidate: false });
  }, [domains, defaultDomainId, isOpen]); // eslint-disable-line

  const domainName = domains.find((d) => d.domainId === chosen)?.domain ?? '';
  const localPart = (createForm.watch('localPart') ?? '').trim().toLowerCase();
  const fullAddress = localPart && domainName ? `${localPart}@${domainName}` : '';

  const close = () => { createForm.reset(); setProblem(null); onClose(); };

  const submitCreate = createForm.handleSubmit(async (v) => {
    setProblem(null);
    try {
      await create.mutateAsync({
        localPart: v.localPart.trim().toLowerCase(), domainId: v.domainId,
        displayName: v.displayName, password: v.password, quotaBytes: Math.round(v.quotaGb * GB),
      });
      close();
    } catch (e) {
      const p = createMailboxError(e, fullAddress, domainName);
      if (p.field) createForm.setError(p.field, { type: 'server', message: p.message }, { shouldFocus: true });
      else setProblem(p);
    }
  });
  const submitEdit = editForm.handleSubmit(async (v) => {
    await update.mutateAsync({ displayName: v.displayName, status: v.status, quotaBytes: Math.round(v.quotaGb * GB) });
    onClose();
  });

  const noDomains = !isEdit && !summary.isLoading && !summary.isError && domains.length === 0;
  const errs = createForm.formState.errors;

  return (
    <Modal isOpen={isOpen} onClose={close} size="lg" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="text-fg">{isEdit ? 'Edit mailbox' : 'Create mailbox'}</ModalHeader>
        <ModalBody>
          {isEdit ? (
            <form id="mailbox-form" onSubmit={submitEdit} className="space-y-4" noValidate>
              <Input isReadOnly label="Address" value={mailbox!.address} variant="flat" />
              <Input {...editForm.register('displayName')} label="Display name" variant="bordered"
                isInvalid={!!editForm.formState.errors.displayName} errorMessage={editForm.formState.errors.displayName?.message} />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Select label="Status" variant="bordered" selectedKeys={new Set([editForm.watch('status')])}
                  onSelectionChange={(k) => editForm.setValue('status', String(Array.from(k)[0]) as 'active' | 'suspended')}>
                  <SelectItem key="active">Active</SelectItem>
                  <SelectItem key="suspended">Suspended</SelectItem>
                </Select>
                <Input {...editForm.register('quotaGb')} type="number" label="Quota (GB)" variant="bordered" min={0}
                  isInvalid={!!editForm.formState.errors.quotaGb} errorMessage={editForm.formState.errors.quotaGb?.message} />
              </div>
            </form>
          ) : summary.isLoading ? (
            <div className="flex items-center gap-3 py-6 text-sm text-fg-muted"><Spinner size="sm" /> Loading your domains…</div>
          ) : summary.isError ? (
            <div role="alert" className="rounded-lg border border-danger/30 bg-danger/10 p-3 text-sm text-fg">
              Your domains could not be loaded, so a mailbox cannot be created right now.{' '}
              <button type="button" className="font-medium text-brand underline" onClick={() => summary.refetch()}>Try again</button>
            </div>
          ) : noDomains ? (
            <div role="status" className="rounded-lg border border-border bg-surface-raised p-4 text-sm text-fg-muted">
              None of your domains is ready for email yet. Add and verify a domain first.{' '}
              <Link to="/domains" className="font-medium text-brand underline" onClick={close}>Go to Domains</Link>
            </div>
          ) : (
            <form id="mailbox-form" onSubmit={submitCreate} className="space-y-4" noValidate>
              {problem && <div role="alert" className="rounded-lg border border-danger/30 bg-danger/10 p-3 text-sm text-fg">{problem.message}</div>}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:items-start">
                <Input {...createForm.register('localPart')} label="Mailbox name" placeholder="info" variant="bordered"
                  autoComplete="off" spellCheck="false" description="The part before the @"
                  isInvalid={!!errs.localPart} errorMessage={errs.localPart?.message} />
                <span aria-hidden="true" className="hidden pt-4 text-lg text-fg-muted sm:block">@</span>
                <Select label="Domain" variant="bordered" aria-describedby="mailbox-address-preview"
                  selectedKeys={chosen ? new Set([chosen]) : new Set()}
                  onSelectionChange={(k) => { createForm.setValue('domainId', String(Array.from(k)[0] ?? ''), { shouldValidate: true }); setProblem(null); }}
                  isInvalid={!!errs.domainId} errorMessage={errs.domainId?.message}>
                  {domains.map((d) => <SelectItem key={d.domainId}>{d.domain}</SelectItem>)}
                </Select>
              </div>
              <p id="mailbox-address-preview" className="rounded-lg bg-surface-raised px-3 py-2 text-sm" aria-live="polite">
                <span className="text-fg-muted">Email address: </span>
                <span className="font-medium text-fg break-all">{fullAddress || '—'}</span>
              </p>
              <Input {...createForm.register('displayName')} label="Display name" variant="bordered"
                isInvalid={!!errs.displayName} errorMessage={errs.displayName?.message} />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Input {...createForm.register('password')} type="password" label="Initial password" variant="bordered" autoComplete="new-password"
                  isInvalid={!!errs.password} errorMessage={errs.password?.message} />
                <Input {...createForm.register('quotaGb')} type="number" label="Quota (GB)" variant="bordered" min={0}
                  isInvalid={!!errs.quotaGb} errorMessage={errs.quotaGb?.message} />
              </div>
            </form>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={close}>Cancel</Button>
          <Button color="primary" type="submit" form="mailbox-form" isDisabled={!isEdit && (noDomains || summary.isLoading || summary.isError)}
            isLoading={isEdit ? editForm.formState.isSubmitting : createForm.formState.isSubmitting}>
            {isEdit ? 'Save changes' : 'Create mailbox'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
